/**
 * Tiny terminal helpers: ANSI colour, status lines, and a spinner. Kept local
 * so the CLI has no third-party UI dependency.
 *
 * Colour is gated per destination stream: `color` for stdout, `errColor` for
 * stderr — so redirecting one stream never leaks escape codes into the other.
 *
 * Every output call also passes through an optional {@link OutputSink}. The
 * interactive chatbox (the fixed bottom input bar) installs itself as the sink
 * with {@link setOutputSink}; that single hook makes every existing `line` /
 * `info` / `out` / `startSpinner` call across the agent loop AND the slash
 * commands redraw ABOVE the input bar instead of trampling it — no per-callsite
 * edits required. With no sink installed (the default, and every non-interactive
 * subcommand) behaviour is byte-for-byte what it always was: stdout for content,
 * stderr for notes.
 */

import { sanitizeAiErrorMessage } from '@fim/futurex-protocol';

/** ASCII escape (0x1B) — derived from char code to avoid literal-escape issues. */
const ESC = String.fromCharCode(27);

type Palette = Record<
  | 'bold'
  | 'dim'
  | 'italic'
  | 'underline'
  | 'strike'
  | 'red'
  | 'green'
  | 'yellow'
  | 'blue'
  | 'magenta'
  | 'cyan'
  | 'gray'
  | 'highlight',
  (s: string) => string
>;

function palette(isTty: boolean): Palette {
  const wrap = (code: number) => (s: string) => (isTty ? `${ESC}[${code}m${s}${ESC}[0m` : s);
  return {
    bold: wrap(1),
    dim: wrap(2),
    italic: wrap(3),
    underline: wrap(4),
    strike: wrap(9),
    red: wrap(31),
    green: wrap(32),
    yellow: wrap(33),
    blue: wrap(34),
    magenta: wrap(35),
    cyan: wrap(36),
    gray: wrap(90),
    // A subtle filled background band (resets ONLY the background with 49m, so the
    // caller can pass already-coloured text without the reset clobbering it).
    highlight: (s: string) => (isTty ? `${ESC}[48;5;237m${s}${ESC}[49m` : s),
  };
}

export const color: Palette = palette(process.stdout.isTTY === true);
export const errColor: Palette = palette(process.stderr.isTTY === true);

/**
 * A consumer that intercepts CLI output so a full-screen-ish TUI (the chatbox)
 * can own the bottom of the terminal. Installed via {@link setOutputSink}.
 */
export interface OutputSink {
  /** A complete line of committed output (the sink adds the newline + layout). */
  line(text: string): void;
  /** A streaming fragment with no implied newline (assistant tokens). */
  partial(text: string): void;
  /** Show/replace a spinner with this label, or clear it when null. */
  spinner(label: string | null): void;
}

let sink: OutputSink | null = null;

/** Route all subsequent output through `s` (the chatbox), or back to the raw streams when null. */
export function setOutputSink(s: OutputSink | null): void {
  sink = s;
}

/** Write to stdout without a trailing newline (used for streaming the answer). */
export function out(text: string): void {
  if (sink) sink.partial(text);
  else process.stdout.write(text);
}

/** Write to stderr without a trailing newline (used for the streamed thinking lane). */
export function eout(text: string): void {
  if (sink) sink.partial(text);
  else process.stderr.write(text);
}

/** Print a line to stdout. */
export function line(text = ''): void {
  if (sink) sink.line(text);
  else process.stdout.write(`${text}\n`);
}

/** Print an informational note to stderr (so it never pollutes piped stdout). */
export function info(text: string): void {
  if (sink) sink.line(color.dim(text));
  else process.stderr.write(`${errColor.dim(text)}\n`);
}

/**
 * Print an error to stderr. Every error line is brand-scrubbed as a last
 * resort: no raw provider error (name, URL, upstream balance) may reach the
 * terminal, whatever path it took to get here.
 */
export function error(text: string): void {
  const scrubbed = sanitizeAiErrorMessage(text);
  if (sink) sink.line(`${color.red('error')} ${scrubbed}`);
  else process.stderr.write(`${errColor.red('error')} ${scrubbed}\n`);
}

/** Print a warning to stderr (yellow). */
export function warn(text: string): void {
  if (sink) sink.line(`${color.yellow('warning')} ${text}`);
  else process.stderr.write(`${errColor.yellow('warning')} ${text}\n`);
}

// --- shared visual language ---------------------------------------------------
//
// One vocabulary for the whole CLI so the agent loop, the gateway client, and
// the slash commands all render identically: phases are dim rules with a bold
// label, reads are cyan `▸`, writes are yellow `✎`, results hang under the call
// as a dim `↳` line, and verification verdicts are green ✓ / red ✗.

/** Compact human token/number count: 950 → "950", 12_345 → "12.3k", 1.2M…. */
export function formatCount(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) {
    const k = n / 1000;
    return `${k >= 100 ? Math.round(k) : k.toFixed(1).replace(/\.0$/, '')}k`;
  }
  const m = n / 1_000_000;
  return `${m >= 100 ? Math.round(m) : m.toFixed(1).replace(/\.0$/, '')}M`;
}

/** A dim horizontal rule with a phase label: `─── build ───…`. */
export function phaseRule(label: string): string {
  const bar = '─'.repeat(3);
  const rest = '─'.repeat(Math.max(4, 38 - label.length));
  return color.dim(`${bar} `) + color.bold(label) + color.dim(` ${rest}`);
}

/** Print a phase transition (plan / build / verify / repair). */
export function phase(label: string): void {
  line(phaseRule(label));
}

/** One tool invocation: yellow ✎ for mutations, cyan ▸ for reads. */
export function toolCallLine(preview: string, mutating: boolean): string {
  return `  ${mutating ? color.yellow('✎') : color.cyan('▸')} ${preview}`;
}

/** The result line that hangs under a tool call: `    ↳ first line …(+n lines)`. */
export function toolResultLine(output: string, ok = true): string {
  const rows = output.split('\n');
  const head = (rows[0] ?? '').slice(0, 88);
  const more = rows.length > 1 ? ` ${color.dim(`…(+${rows.length - 1} lines)`)}` : '';
  const mark = ok ? color.dim('↳') : color.red('↳');
  return `    ${mark} ${color.dim(head || (ok ? '(ok)' : '(failed)'))}${more}`;
}

/** A green-check / red-cross verdict line (verification output). */
export function verdictLine(ok: boolean, text: string): string {
  return `  ${ok ? color.green('✓') : color.red('✗')} ${text}`;
}

const SPINNER_FRAMES = ['⠋', '⠙', '⠹', '⠸', '⠼', '⠴', '⠦', '⠧', '⠇', '⠏'];

/** A running spinner on stderr; call stop() to clear it. No-op off a TTY. */
export interface Spinner {
  stop(): void;
}

/**
 * Start a Braille spinner with a dim label. When a sink (the chatbox) is
 * installed the spinner is delegated to it; otherwise it animates on stderr
 * (and is a no-op when stderr isn't a TTY).
 */
export function startSpinner(label: string): Spinner {
  const installed = sink;
  if (installed) {
    installed.spinner(label);
    return {
      stop() {
        installed.spinner(null);
      },
    };
  }
  if (process.stderr.isTTY !== true) return { stop() {} };
  let i = 0;
  process.stderr.write(`${ESC}[?25l`); // hide cursor
  const render = (): void => {
    const frame = SPINNER_FRAMES[i % SPINNER_FRAMES.length] ?? '';
    process.stderr.write(`\r${errColor.cyan(frame)} ${errColor.dim(label)}${ESC}[K`);
    i += 1;
  };
  render();
  const timer = setInterval(render, 80);
  timer.unref?.();
  return {
    stop() {
      clearInterval(timer);
      process.stderr.write(`\r${ESC}[K${ESC}[?25h`); // clear line + restore cursor
    },
  };
}
